import { useEffect, useState } from "react";
import { AlertCircle, PlusCircle, Trash2, Save, X } from "lucide-react";
import { api, type ApiCategory } from "@/lib/api";
import { SortableList } from "@/components/SortableList";

export default function AdminCategoriesTab() {
  const [cats, setCats] = useState<ApiCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [form, setForm] = useState({ name: "", sort_order: 0 });
  const [editing, setEditing] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<{ name: string; sort_order: number }>({ name: "", sort_order: 0 });

  function load() {
    setLoading(true);
    api.listCategories()
      .then((r) => setCats(r.categories))
      .catch((e) => setErr((e as Error).message))
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, []);

  async function add() {
    setErr(null);
    if (!form.name.trim()) { setErr("名称必填"); return; }
    try {
      await api.createCategory(form.name.trim(), Number(form.sort_order) || 0);
      setForm({ name: "", sort_order: 0 });
      load();
    } catch (e) { setErr((e as Error).message); }
  }

  async function save(id: number) {
    try {
      await api.updateCategory(id, { name: editDraft.name.trim(), sort_order: Number(editDraft.sort_order) || 0 });
      setEditing(null);
      load();
    } catch (e) { alert((e as Error).message); }
  }

  async function del(c: ApiCategory) {
    if (!confirm(`确认删除分类 "${c.name}"？`)) return;
    try {
      await api.deleteCategory(c.id);
      load();
    } catch (e) { alert((e as Error).message); }
  }

  return (
    <>
      <div className="card mb-6 p-5">
        <div className="mb-3 text-sm font-semibold text-ink-900">新增分类</div>
        <div className="flex flex-col gap-3 md:flex-row md:items-end">
          <label className="flex-1">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-ink-600">名称</span>
            <input className="input" placeholder="例如：OET Speaking 专项" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </label>
          <label className="w-32">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-ink-600">排序</span>
            <input className="input" type="number" placeholder="0" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} />
          </label>
          <button className="btn-primary md:w-auto" onClick={add}>
            <PlusCircle className="h-4 w-4" /> 添加
          </button>
        </div>
        <p className="mt-2 text-[11px] text-ink-400">slug 自动生成（基于名称）。排序值小的靠前。</p>
      </div>

      {err && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle className="h-4 w-4" /> {err}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-surface-border bg-white">
        <div className="border-b border-surface-border bg-surface-muted px-5 py-3 text-[11px] uppercase tracking-wider text-ink-400">
          分类列表 · 拖动 <span className="font-mono">⋮⋮</span> 调整顺序（前台筛选 chips 与视频库分组都按这个顺序）
        </div>
        {loading ? (
          <div className="px-5 py-12 text-center text-sm text-ink-400">加载中…</div>
        ) : cats.length === 0 ? (
          <div className="px-5 py-12 text-center text-sm text-ink-400">暂无分类，请先新增</div>
        ) : (
          <SortableList
            items={cats}
            onReorder={async (orderedIds) => {
              await api.reorderCategories(orderedIds);
              load();
            }}
            renderItem={(c) =>
              editing === c.id ? (
                <div className="flex items-center gap-3 px-3 py-2">
                  <input
                    className="input !py-1.5 !text-sm flex-1"
                    value={editDraft.name}
                    onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                  />
                  <span className="font-mono text-xs text-ink-400">{c.slug}</span>
                  <button onClick={() => save(c.id)} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-brand hover:bg-brand-light">
                    <Save className="h-3 w-3" /> 保存
                  </button>
                  <button onClick={() => setEditing(null)} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-ink-400 hover:bg-surface-muted">
                    <X className="h-3 w-3" /> 取消
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3 px-3 py-3">
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-sm font-medium text-ink-900">{c.name}</div>
                    <div className="truncate font-mono text-[10px] text-ink-400">{c.slug}</div>
                  </div>
                  <button
                    onClick={() => { setEditing(c.id); setEditDraft({ name: c.name, sort_order: c.sort_order }); }}
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-ink-600 hover:bg-surface-muted"
                  >
                    编辑
                  </button>
                  <button
                    onClick={() => del(c)}
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-red-500 hover:bg-red-50"
                  >
                    <Trash2 className="h-3 w-3" /> 删除
                  </button>
                </div>
              )
            }
          />
        )}
      </div>
    </>
  );
}
